import { devtoolsInNonProd } from "@util/helper";
import { create } from "zustand";
import { persist } from "zustand/middleware";

/** Form inputs by form ID. File uploads are ignored (not stored). */
export type FormProgressState = Record<string, Record<string, unknown>>;

interface FormProgressStore {
  byFormId: FormProgressState;
  getFormInputs: (formId: string) => Record<string, unknown>;
  setFormInputs: (formId: string, inputs: Record<string, unknown>) => void;
  updateField: (formId: string, key: string, value: unknown) => void;
  clearForm: (formId: string) => void;
}

/** Omit File values and error keys – store ignores file uploads. */
function withoutFiles(inputs: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(inputs)) {
    if (v instanceof File || k.endsWith("Error")) continue;
    out[k] = v;
  }
  return out;
}

const useFormProgressStore = create<FormProgressStore>()(
  devtoolsInNonProd(
    persist(
      (set, get) => ({
        byFormId: {},

        getFormInputs: (formId: string) => get().byFormId[formId] ?? {},

        setFormInputs: (formId: string, inputs: Record<string, unknown>) => {
          const persisted = withoutFiles(inputs);
          set(state => ({
            byFormId: {
              ...state.byFormId,
              [formId]: { ...(state.byFormId[formId] ?? {}), ...persisted },
            },
          }));
        },

        updateField: (formId: string, key: string, value: unknown) => {
          if (value instanceof File || key.endsWith("Error")) return;
          set(state => {
            const prev = state.byFormId[formId] ?? {};
            const next = { ...prev, [key]: value };
            if (value === undefined || value === null) {
              delete next[key];
            }
            delete next[`${key}Error`];
            return {
              byFormId: { ...state.byFormId, [formId]: next },
            };
          });
        },

        clearForm: (formId: string) => {
          set(state => {
            const next = { ...state.byFormId };
            delete next[formId];
            return { byFormId: next };
          });
        },
      }),
      {
        name: "nzas-form-progress",
        partialize: state => ({ byFormId: state.byFormId }),
      },
    ),
  ),
);

export default useFormProgressStore;
